import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSparePartSelectionStore from "store/useSparePartSelectionStore";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { yupResolver } from "@hookform/resolvers/yup";
import { createWorkOrder, duplicateWorkOrder, getWorkOrder, getWorkOrderActions, updateWorkOrder } from "api/workOrders";
import CustomReactSelect from "components/CustomReactSelect";
import BaseTable from "components/DataTable/BaseTable";
import HeaderCard from "components/DataTable/HeaderCard";
import ImageUploadField from "components/ImageUploadField";
import Loading from "components/Loading";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PriorityGroupButton from "components/PriorityGroupButton";
import RideSelector from "components/RideSelector";
import SparePartsMultiSelectModal from "components/SparePartsMultiSelectModal";
import StaffSelector from "components/StaffSelector";
import WorkOrderCategorySelector from "components/WorkOrderCategorySelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";
import { formatDateTime, getPriorityFilters, getProgressStatusFilters, getWorkOrderStatusFilters, jsonToFormData, PROGRESS_COMPLETED, PROGRESS_OPEN, STATUS_COMPLETED, WORK_ORDER_ACTION, WorkOrderType } from "utils/helper";
import * as yup from "yup";
import { parentRoute } from ".";

const progressStatusSelection = getProgressStatusFilters(false)
const statusSelection = getWorkOrderStatusFilters(false)
const workOrderTypeSelection = WorkOrderType ? Object.keys(WorkOrderType).map(key => ({ value: WorkOrderType[key], label: key.charAt(0) + key.slice(1).toLowerCase() })) : [];

const TABLE_COLUMNS = [
    { id: "action", Header: "Action", accessor: "action", align: "left" },
    { id: "old_value", Header: "Old Value", accessor: "old_value", align: "left" },
    { id: "new_value", Header: "New Value", accessor: "new_value", align: "left" },
    { id: "user_id", Header: "Performed By", accessor: "user", align: "left" },
    { id: "created_at", Header: "Performed At", accessor: "created_at", align: "left", sortable: true },
];

const workOrderSchema = yup.object({
    wo_title: yup.string().trim().required("Title is required").max(150, "Max 150 characters"),
    wo_category: yup.string(),
    wo_description: yup.string().max(200, "Max 200 characters"),
    wo_work_type: yup.string().required("Work type is required"),
    wo_start_date: yup.string().when("wo_work_type", {
        is: WorkOrderType.PREVENTIVE,
        then: schema => schema.required("Start date is required for preventive work order"),
        otherwise: schema => schema.nullable(),
    }),
    wo_due_date: yup.string().nullable(),
    wo_progress_status: yup.string().required("Progress status is required"),
    wo_completion_note: yup.string().max(200, "Max 200 characters").when("wo_progress_status", {
        is: PROGRESS_COMPLETED,
        then: schema => schema.required("Completion note is required when marking as completed"),
        otherwise: schema => schema.nullable(),
    }),
});

const AddWorkOrder = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const { item, fromInspection, returnState, returnPathname } = location.state || {}
    const [formData, setFormData] = useState({
        wo_title: "",
        wo_priority: "",
        wo_ride_id: "",
        wo_category: "",
        wo_due_date: "",
        wo_image: "",
        wo_description: "",
        wo_sparepart_id: [], // Array of {id, name, qty, stock}
        wo_progress_status: "",
    });
    // Zustand store for selected spare parts
    const { selectedParts, setSelectedParts, clearSelectedParts } = useSparePartSelectionStore();
    const [isLoading, setIsLoading] = useState(false);
    const [workOrderDetail, setWorkOrderDetail] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [imageArr, setImageArr] = useState([]);
    const [selectSparePartsModal, setSelectSparePartsModal] = useState(false);
    const [refreshEvent, setRefreshEvent] = useState(0);
    const id = Number(location.pathname.split('/')[2]);

    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add`),
        [location.pathname]
    );

    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(workOrderSchema),
        mode: "onSubmit",
        defaultValues: {
            wo_title: "",
            wo_category: "",
            wo_description: "",
            wo_work_type: "",
            wo_ride_id: "",
            wo_due_date: "",
            wo_progress_status: "",
            wo_status: "",
            wo_assigned_user_id: [],
            wo_priority: "",
            wo_sparepart_id: [],
            wo_image: "",
            wo_completion_note: "",
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        console.log("Mapped Errors: ", mapped);
        setFormErrors(mapped);
    }, [rhfErrors]);

    useEffect(() => {
        const fetchData = async () => {
            if (isAdd) {
                if (fromInspection) {
                    setWorkOrderDetail({ inspection_checklist_item: location.state?.inspection_checklist_item });
                    setFormData({ ...formData, wo_ride_id: location.state?.rideId || "" })
                }
                setFormData({
                    ...formData,
                    wo_progress_status: PROGRESS_OPEN,
                    wo_work_type: WorkOrderType.REACTIVE,
                });
                return;
            }

            if (!id || isNaN(id)) {
                navigate(parentRoute, { state: location.state });
                return;
            }
            const itemId = id;

            setIsLoading(true);
            try {
                const { data: details } = await getWorkOrder(itemId);
                setWorkOrderDetail(details);
                if (details) {
                    setImageArr(details.img_url ?? []);
                    let spartParts = details.spare_parts?.map(sp => ({ id: sp?.id, name: sp.name, qty: sp.pivot?.quantity, stock: sp.stock })) || []
                    await setFormData({
                        id: details.id,
                        wo_title: details.title || "",
                        wo_priority: details.priority?.id || "",
                        wo_ride_id: details.ride?.id || "",
                        wo_category: details.category || "",
                        wo_start_date: details.start_date || "",
                        wo_due_date: details.due_date || "",
                        wo_image: details.img_filename || "",
                        wo_description: details.description || "",
                        wo_progress_status: details.progress_status?.id || "",
                        wo_status: details.status?.id || "",
                        wo_assigned_user_id: details.assigned_users?.map(user => user.id) || [],
                        wo_sparepart_id: spartParts,
                        wo_work_type: details.work_type?.value || WorkOrderType.REACTIVE,
                        wo_completion_note: details.completion_note || "",
                    });
                    setSelectedParts(spartParts);
                }
            } catch (error) {
                toast.error("Failed to find vendor data");
                navigate(parentRoute, { state: location.state });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        return () => {
            clearSelectedParts();
        }
    }, [isAdd, location.pathname, location.state]);

    const formatFieldValue = useCallback((item, isOldValue = false) => {
        const value = isOldValue ? item.old_value : item.new_value;

        // Handle null or undefined values
        if (value === null || value === undefined) {
            return '-';
        }

        switch (item.action?.id) {
            case WORK_ORDER_ACTION.STATUS_CHANGE:
                return getWorkOrderStatusFilters().find(f => f.value.toString() === value)?.label || value || '-';
            case WORK_ORDER_ACTION.PROGRESS_CHANGE:
                if (typeof value === "object" && value !== null) {
                    return value?.name || '-';
                }
                return getProgressStatusFilters().find(f => f.value.toString() === value)?.label || value || '-';
            case WORK_ORDER_ACTION.PRIORITY_CHANGE:
                return getPriorityFilters().find(f => f.value.toString() === value)?.label || '-';
            case WORK_ORDER_ACTION.WORK_TYPE_CHANGE:
                return workOrderTypeSelection.find(type => type.value === value)?.label || value || '-';
            case WORK_ORDER_ACTION.DUPLICATED:
                return 'Duplicated from Work Order';
            case WORK_ORDER_ACTION.DUE_DATE_CHANGE:
            case WORK_ORDER_ACTION.START_DATE_CHANGE:
                return value ? new Date(value).toLocaleDateString() : '-';
            case WORK_ORDER_ACTION.IMAGE_CHANGE:
                return value ? 'Image updated' : 'Image removed';
            case WORK_ORDER_ACTION.ASSIGN:
                if (Array.isArray(value)) {
                    if (value.length === 0) {
                        return '-';
                    }
                    return value.map(user => user?.full_name || '-').join(', ');
                }
                if (typeof value === "object" && value !== null) {
                    return value?.full_name || '-';
                }
                return value || '-';
            case WORK_ORDER_ACTION.ATTACH_SPAREPART:
            case WORK_ORDER_ACTION.DETACH_SPAREPART:
                return (item?.sparepart?.name ?? "-") + ` (Qty: ${item?.attached_quantity ?? item?.new_value ?? 0})`;
            case WORK_ORDER_ACTION.UPDATE_SPAREPART_QUANTITY:
                return (item?.sparepart?.name ?? "-") + ` (Qty: ${value ?? 0})`;
            case WORK_ORDER_ACTION.SCAN_SPAREPART:
                return (item?.sparepart?.name ?? "-") + ` (Qty: ${value ?? 0})`;
            default:
                break;
        }

        // Handle object values
        if (typeof value === "object" && value !== null) {
            return value?.name || value?.full_name || '-';
        }

        // Handle primitive values
        return value ?? '-';
    }, []);

    const createTableRow = useCallback((item) => ({
        action: <MDTypography component="div" variant="standard" color="text">{item.action?.name}</MDTypography>,
        old_value: <MDTypography component="div" variant="standard" color="text">
            {formatFieldValue(item, true)}
        </MDTypography>,
        new_value: <MDTypography component="div" variant="standard" color="text">
            {formatFieldValue(item, false)}
        </MDTypography>,
        user: <MDTypography component="div" variant="standard" color="text">{item.user?.full_name ?? '-'}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>
    }), [formatFieldValue]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsLoading(true);
        // if work order is preventive and updating to completed
        if (!isAdd && formData.wo_work_type === WorkOrderType.PREVENTIVE && formData.wo_status === STATUS_COMPLETED && workOrderDetail?.status?.id !== STATUS_COMPLETED) {
            const { value: nextDate } = await Swal.fire({
                title: 'Next Preventive Work Order',
                text: 'This preventive work order will be completed. Please enter the start date for the next preventive work order:',
                input: 'date',
                inputValue: '',
                showCancelButton: true,
                confirmButtonText: 'Create Next Work Order',
                cancelButtonText: 'Complete Only',
                inputValidator: (value) => {
                    if (!value) {
                        return 'Please select a date';
                    }
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (selectedDate < today) {
                        return 'Date cannot be in the past';
                    }
                }
            });

            if (nextDate) {
                try {
                    await duplicateWorkOrder(workOrderDetail.id, { wo_start_date: nextDate })
                    await toast.success("Next preventive work order created successfully");
                } catch (error) {
                    console.error("Error creating next preventive work order:", error);
                    return
                }
            }
        }
        let processedFormData = {
            ...formData,
            wo_sparepart_id: formData.wo_sparepart_id?.map(sp => sp.id) || [],
            wo_sparepart_qty: formData.wo_sparepart_id?.map(sp => sp.qty) || [],
        };

        // Ensure empty arrays are passed for spare parts fields even when no parts are selected
        if (formData.wo_sparepart_id.length === 0) {
            processedFormData.wo_sparepart_id = [];
            processedFormData.wo_sparepart_qty = [];
        }

        if (!isAdd) {
            delete processedFormData.id;
            delete processedFormData.wo_title;
            delete processedFormData.wo_ride_id;
            delete processedFormData.wo_category;
            delete processedFormData.wo_description;
        } else {
            if (fromInspection) {
                processedFormData.wo_inspection_checklist_item_id = workOrderDetail?.inspection_checklist_item?.id || null
            }
        }

        if (imageArr != null) {
            processedFormData.wo_image = imageArr;
        }
        setFormErrors({});
        try {
            let passData = jsonToFormData(processedFormData)

            if (isAdd) {
                const result = await Swal.fire({
                    title: 'Confirmation',
                    text: 'Are you sure you want to create this work order? There are some fields unable to edit after created.',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonColor: '#3085d6',
                    cancelButtonColor: '#d33',
                    confirmButtonText: 'Yes, create it!'
                });

                if (result.isConfirmed) {
                    await createWorkOrder(passData);
                } else {
                    return;
                }
                toast.success("Work Order added successfully");
                navigate(parentRoute, { state: location.state });
            } else {
                await updateWorkOrder(workOrderDetail.id, passData);
                toast.success("Work Order updated successfully");
                setRefreshEvent(prev => prev + 1);
            }
        } catch (err) {
            setFormErrors(err?.data?.errors || {});
            toast.error(err?.data?.message || (isAdd ? "Failed to create work order" : "Failed to update work order"));
        } finally {
            setIsLoading(false);
        }
    }, [formData, navigate, isAdd, imageArr, item]);

    const handleValidatedSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setFormErrors({});
            const valid = await trigger();
            if (!valid) {
                toast.error("Please fix the validation errors before submitting.");
                return;
            }
            await handleSubmit(e); // your original submit logic
        },
        [trigger, handleSubmit]
    );

    const fetchActionLogs = useCallback(async (params) => {
        if (!workOrderDetail?.id) return;
        return getWorkOrderActions(workOrderDetail?.id, params);
    }, [workOrderDetail?.id]);

    const WorkOrderActionTable = useMemo(() => {
        if (isAdd) return null;
        return (
            <Grid item xs={12} mt={-4}>
                <BaseTable
                    key={refreshEvent}
                    title="Work Orders Action Log"
                    fetchData={fetchActionLogs}
                    columns={TABLE_COLUMNS}
                    createTableRow={createTableRow}
                />
            </Grid>
        );
    }, [isAdd, refreshEvent, workOrderDetail]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={4} pb={3}>
                <Grid container spacing={7}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Create a New Work Order" : "Edit Work Order"}
                                showBackButton={true}
                                onBackClick={() => { returnPathname ? navigate(returnPathname, { state: returnState }) : navigate(`${parentRoute}${location.search}`, { state: location.state }); setPrevSelectedRow(workOrderDetail?.id, parentRoute) }}
                            />
                            {isLoading && (!isAdd && !workOrderDetail) ? <Loading /> :
                                <MDBox pt={3} px={2} pb={2}>
                                    {!isAdd && (
                                        <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                            <MDTypography variant="body2" color="text" gutterBottom>Created By: {workOrderDetail?.user?.full_name}</MDTypography>
                                            <MDTypography variant="body2" color="text" gutterBottom>
                                                Created At: {workOrderDetail?.created_at ? formatDateTime(workOrderDetail.created_at) : '-'}
                                            </MDTypography>
                                            <MDTypography variant="body2" color="text" gutterBottom>
                                                Last Updated: {workOrderDetail?.updated_at ? formatDateTime(workOrderDetail.updated_at) : '-'}
                                            </MDTypography>
                                        </MDBox>
                                    )}
                                    {workOrderDetail?.inspection_checklist_item != null && (
                                        <MDBox mb={4}>
                                            <MDTypography variant="h6" gutterBottom>Reported Issue</MDTypography>
                                            <MDBox mb={3} p={1.5} border={1} borderColor="grey.300" borderRadius="md" bgcolor="grey.50">
                                                <Grid container spacing={1} className="inspection-table">
                                                    <Grid item xs={12} md={4}>
                                                        <MDTypography variant="caption"><b>Report ID:</b></MDTypography>
                                                        <MDTypography variant="body2">{workOrderDetail.inspection_checklist_item?.inspection_report?.report_id}</MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <MDTypography variant="caption"><b>Reported By:</b></MDTypography>
                                                        <MDTypography variant="body2">
                                                            {workOrderDetail.inspection_checklist_item?.inspection_report?.user?.full_name
                                                                || "-"}
                                                        </MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <MDTypography variant="caption"><b>Reported On:</b></MDTypography>
                                                        <MDTypography variant="body2">
                                                            {workOrderDetail.inspection_checklist_item?.inspection_report?.created_at
                                                                ? new Date(workOrderDetail.inspection_checklist_item.inspection_report.created_at).toLocaleString()
                                                                : "-"}
                                                        </MDTypography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <MDTypography variant="caption"><b>Inspection check item:</b></MDTypography>
                                                        <MDTypography variant="body2" >{workOrderDetail.inspection_checklist_item?.checklist_item?.name}</MDTypography>
                                                    </Grid>
                                                </Grid>
                                            </MDBox>
                                        </MDBox>
                                    )}

                                    <form noValidate onSubmit={handleValidatedSubmit} style={{ display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
                                        <Grid item xs={12}>
                                            <Grid container spacing={3} style={{ flexGrow: 1 }}>
                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Work Order Title"
                                                        name="wo_title"
                                                        value={formData?.wo_title || ''}
                                                        onChange={handleChange}
                                                        required
                                                        disabled={!isAdd}
                                                        helperText={formErrors.wo_title}
                                                        error={Boolean(formErrors.wo_title)}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <WorkOrderCategorySelector
                                                        fullWidth
                                                        label="Category"
                                                        name="wo_category"
                                                        value={formData?.wo_category || ''}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'wo_category', value: selectedOption?.value ?? selectedOption } })}
                                                        creatable
                                                        error={Boolean(formErrors.wo_category)}
                                                        helperText={formErrors.wo_category}
                                                        disabled={!isAdd}
                                                    />
                                                </Grid>


                                                <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Description"
                                                        name="wo_description"
                                                        value={formData?.wo_description || ''}
                                                        onChange={handleChange}
                                                        multiline
                                                        minRows={2}
                                                        inputProps={{ maxLength: 200 }}
                                                        disabled={!isAdd}
                                                    />
                                                </Grid>

                                                {!fromInspection && <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Work Type"
                                                        name="wo_work_type"
                                                        variant="outlined"
                                                        value={workOrderTypeSelection.find(type => type.value === (formData?.wo_work_type ?? WorkOrderType.REACTIVE))}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'wo_work_type', value: selectedOption?.value } })}
                                                        options={workOrderTypeSelection}
                                                        required
                                                    />
                                                </Grid>}

                                                {!fromInspection && <Grid item xs={12} md={6}>
                                                    <RideSelector
                                                        fullWidth
                                                        label="Ride"
                                                        name="wo_ride_id"
                                                        value={formData?.wo_ride_id || ''}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'wo_ride_id', value: selectedOption } })}
                                                        required={false}
                                                        isClearable
                                                        disabled={!isAdd}
                                                    />
                                                </Grid>}

                                                {formData?.wo_work_type === WorkOrderType.PREVENTIVE && <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Start Date"
                                                        name="wo_start_date"
                                                        type="date"
                                                        value={formData?.wo_start_date || ''}
                                                        onChange={handleChange}
                                                        InputLabelProps={{ shrink: true }}
                                                        helperText={formErrors.wo_start_date}
                                                        error={Boolean(formErrors.wo_start_date)}
                                                        inputProps={{ min: isAdd ? new Date().toISOString().split('T')[0] : undefined }}
                                                        required
                                                    />
                                                </Grid>}

                                                <Grid item xs={12} md={6}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Due Date"
                                                        name="wo_due_date"
                                                        type="date"
                                                        value={formData?.wo_due_date || ''}
                                                        onChange={handleChange}
                                                        InputLabelProps={{ shrink: true }}
                                                        helperText={formErrors.wo_due_date}
                                                        error={Boolean(formErrors.wo_due_date)}
                                                        inputProps={{ min: isAdd ? new Date().toISOString().split('T')[0] : undefined }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} md={6}>
                                                    <CustomReactSelect
                                                        label="Progress Status"
                                                        name="wo_progress_status"
                                                        variant="outlined"
                                                        value={progressStatusSelection.find(status => status.value === formData?.wo_progress_status)}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'wo_progress_status', value: selectedOption?.value } })}
                                                        options={progressStatusSelection}
                                                    />
                                                </Grid>

                                                {!isAdd && <>
                                                    <Grid item xs={12} md={6}>
                                                        <CustomReactSelect
                                                            label="Status"
                                                            name="wo_status"
                                                            variant="outlined"
                                                            value={statusSelection.find(status => status.value === formData?.wo_status)}
                                                            onChange={(selectedOption) => handleChange({ target: { name: 'wo_status', value: selectedOption?.value } })}
                                                            options={statusSelection}
                                                        />
                                                    </Grid></>}

                                                {!isAdd && formData?.wo_progress_status === PROGRESS_COMPLETED && <Grid item xs={12}>
                                                    <MDInput
                                                        fullWidth
                                                        label="Completion Note"
                                                        name="wo_completion_note"
                                                        value={formData?.wo_completion_note || ''}
                                                        onChange={handleChange}
                                                        multiline
                                                        minRows={2}
                                                        required
                                                        inputProps={{ maxLength: 200 }}
                                                    />
                                                </Grid>}

                                                <Grid item xs={12}>
                                                    <StaffSelector
                                                        label="Assigned Staff"
                                                        name="wo_assigned_user_id"
                                                        value={formData?.wo_assigned_user_id}
                                                        onChange={(selectedOption) => handleChange({ target: { name: 'wo_assigned_user_id', value: selectedOption } })}
                                                        isMulti
                                                        disabled={formData?.wo_progress_status === PROGRESS_COMPLETED}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} mt={-2.5}>
                                                    <PriorityGroupButton name="wo_priority" value={formData?.wo_priority} handleChange={handleChange} />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <MDBox display="flex" justifyContent="space-between" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                                                        <MDTypography variant="subtitle2"><b>Spare Parts</b></MDTypography>
                                                        <MDButton variant="outlined" color="info" onClick={() => setSelectSparePartsModal(true)}>
                                                            {formData?.wo_sparepart_id?.length > 0 ? `Selected Spare Parts (${formData?.wo_sparepart_id?.length})` : "Select Spare Parts"}
                                                        </MDButton>
                                                    </MDBox>
                                                </Grid>
                                                <SparePartsMultiSelectModal
                                                    open={selectSparePartsModal}
                                                    value={selectedParts}
                                                    rideId={formData?.wo_ride_id}
                                                    onClose={() => setSelectSparePartsModal(false)}
                                                    onChange={(parts) => {
                                                        setSelectedParts(parts);
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            wo_sparepart_id: parts.map(sp => {
                                                                const existing = prev.wo_sparepart_id?.find(p => p.id === sp.id);
                                                                return { ...sp, qty: existing?.qty || 0 };
                                                            })
                                                        }));
                                                    }}
                                                />
                                                {selectedParts.length > 0 && (
                                                    <Grid item xs={12}>
                                                        <MDBox
                                                            component="table"
                                                            className="spare-parts-table"
                                                        >
                                                            <MDBox component="thead">
                                                                <MDBox component="tr">
                                                                    <MDBox component="th">Name</MDBox>
                                                                    {!isAdd && <MDBox component="th" className="center">Qty</MDBox>}
                                                                    <MDBox component="th" className="center stock">Stock</MDBox>
                                                                </MDBox>
                                                            </MDBox>
                                                            <MDBox component="tbody">
                                                                {selectedParts.map((sp, idx) => (
                                                                    <MDBox component="tr" key={sp.id}>
                                                                        <MDBox component="td">{sp.name}</MDBox>
                                                                        {!isAdd && <MDBox component="td" className="center">
                                                                            <MDInput
                                                                                type="number"
                                                                                label=""
                                                                                value={sp.qty || 0}
                                                                                inputProps={{ min: 0, max: sp.stock || undefined, className: 'qty-input' }}
                                                                                onChange={e => {
                                                                                    const qty = parseInt(e.target.value) || 0;
                                                                                    setSelectedParts(selectedParts.map((item, i) =>
                                                                                        i === idx ? { ...item, qty } : item
                                                                                    ));
                                                                                    setFormData(prev => ({
                                                                                        ...prev,
                                                                                        wo_sparepart_id: selectedParts.map((item, i) =>
                                                                                            i === idx ? { ...item, qty } : item
                                                                                        )
                                                                                    }));
                                                                                }}
                                                                            />
                                                                        </MDBox>}
                                                                        <MDBox component="td" className="stock">
                                                                            {sp.stock !== undefined ? sp.stock : '-'}
                                                                        </MDBox>
                                                                    </MDBox>
                                                                ))}
                                                            </MDBox>
                                                        </MDBox>
                                                    </Grid>
                                                )}

                                                <Grid item xs={12}>
                                                    <ImageUploadField
                                                        value={imageArr ?? ""}
                                                        onChange={(file) => setImageArr(file)}
                                                        name="wo_image"
                                                        label="Work Order Image"
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Grid item xs />
                                                    <Grid item>
                                                        <MDBox display="flex" justifyContent="flex-end">
                                                            <MDButton
                                                                variant="contained"
                                                                color="primary"
                                                                type="submit"
                                                                loading={isLoading}
                                                            >
                                                                Submit
                                                            </MDButton>
                                                        </MDBox>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </form>
                                </MDBox>}
                        </Card >
                    </Grid >
                    {WorkOrderActionTable}
                </Grid >
            </MDBox >
        </DashboardLayout >
    );
};

export default AddWorkOrder;